/**
 * Why the caller can't ask the assistant in a shared session (TMMAPP-0164 item 14; TMMAPI-0195 U4):
 * shown at the composer when Send to assistant is activated by a member whose `canAsk` is false, when
 * an ask is refused (`403 ASSISTANT_ASK_NOT_ALLOWED {reason}`), and in place of the composer in their
 * read-only breakout. The sentence is by `canAskReason` (`cantAskCopy`). For a GUEST or NONE tier the
 * owner hasn't allowed, the explanation's action is the purchase experience (M11), with "or ask
 * {owner} to allow it"; the explanation always comes first, and nothing is disabled.
 */
import { Alert, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AssistantAskReason } from '@/api/assistant';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { useMe } from '@/session/meHooks';
import { cantAskCopy } from './assistantCopy';

export function CantAskNotice({
  reason,
  virtualAccountName,
  ownerName,
  onClose,
}: {
  reason: AssistantAskReason | null;
  virtualAccountName: string | null;
  ownerName: string | null;
  /** The person dismisses it (at the composer); a read-only room's has none. */
  onClose?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const openSubscribe = useOpenSubscribe();
  // A subscription can't lift a limit or a switch: the purchase is offered only for not allowed.
  const purchase = me.tier !== 'SUBSCRIBER' && (reason === 'NOT_ALLOWED' || reason === null);
  const owner = ownerName?.trim() ?? '';
  return (
    <Alert
      severity="info"
      data-cant-ask={reason ?? 'UNKNOWN'}
      {...(onClose !== undefined ? { onClose } : {})}
    >
      {cantAskCopy(t, reason, virtualAccountName, ownerName)}
      {purchase && (
        <Box
          component="span"
          data-cant-ask-purchase=""
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}
        >
          <Button size="small" variant="outlined" color="inherit" onClick={openSubscribe}>
            {t('subscribe.cta')}
          </Button>
          <Box component="span">
            {owner.length > 0
              ? t('assistant.cantAsk.orAskOwner', { owner })
              : t('assistant.cantAsk.orAskOwnerUnnamed')}
          </Box>
        </Box>
      )}
    </Alert>
  );
}
